import { Link } from 'react-router-dom';
import { Star, Clock, MapPin, Navigation } from 'lucide-react';
import defaultLogo from '../../../assets/logo.svg';
import { isStoreOpen, formatStoreLocation } from '../merchant.utils';

export interface StoreCardProps {
  id: string;
  name: string;
  bannerUrl?: string | null;
  logoUrl?: string | null;
  location?: string | null;
  city?: string | null;
  stateOrRegion?: string | null;
  rating?: number;
  reviewCount?: number;
  deliveryTime?: string;
  distance?: string;
  distanceInMiles?: number | null;
  openingTime?: string | null;
  closingTime?: string | null;
  openOnSunday?: boolean | null;
  isOpen?: boolean;
}

export function StoreCard({
  id,
  name,
  bannerUrl,
  logoUrl,
  location,
  city,
  stateOrRegion,
  rating,
  reviewCount,
  deliveryTime = "24 hours",
  distance,
  distanceInMiles,
  openingTime,
  closingTime,
  openOnSunday,
  isOpen: explicitIsOpen,
}: StoreCardProps) {
  const computedIsOpen = explicitIsOpen !== undefined 
    ? explicitIsOpen 
    : isStoreOpen(openingTime, closingTime, openOnSunday);

  const formattedDistance = distanceInMiles !== undefined && distanceInMiles !== null
    ? `${distanceInMiles < 0.1 && distanceInMiles > 0 ? '< 0.1' : distanceInMiles.toFixed(1)} ${distanceInMiles === 1 ? 'mile' : 'miles'}`
    : distance;

  const displayLocation = formatStoreLocation(city, stateOrRegion, location);

  return (
    <Link to={`/merchant/${id}`} className="group block h-full">
      <div className={`bg-white dark:bg-slate-900 rounded-[20px] overflow-hidden border border-slate-200 dark:border-slate-800 transition-all duration-300 h-full flex flex-col group-hover:-translate-y-1 p-2 ${!computedIsOpen ? 'opacity-90' : ''}`}>
        {/* Banner Area */}
        <div className="relative h-32 w-full overflow-hidden bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center border border-slate-100 dark:border-slate-800">
          {/* Closed Status Badge */}
          {!computedIsOpen && (
            <div className="absolute top-2.5 right-2.5 z-20">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-900/85 dark:bg-slate-950/85 text-white backdrop-blur-md border border-white/10">
                Closed
              </span>
            </div>
          )}

          {/* Banner Cover Image */}
          {bannerUrl ? (
            <img 
              src={bannerUrl} 
              alt={`${name} banner`} 
              className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${!computedIsOpen ? 'grayscale-[35%]' : ''}`}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-full h-full items-center justify-center p-6 bg-emerald-50/40 dark:bg-slate-800/80 ${bannerUrl ? 'hidden' : 'flex'} ${!computedIsOpen ? 'grayscale-[35%]' : ''}`}
          >
            <img 
              src={defaultLogo} 
              alt="ShopMuchMore Logo" 
              className="max-h-9 w-auto object-contain opacity-70 group-hover:scale-105 transition-transform duration-300" 
            />
          </div>

          {/* Scrim Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent pointer-events-none" />

          {/* Store Logo Badge */}
          <div className="absolute bottom-2.5 left-2.5 z-10">
            <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-900 p-0.5 border border-slate-200/90 dark:border-slate-700/90 overflow-hidden flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={`${name} logo`} 
                  className="w-full h-full object-cover rounded-[9px]"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`w-full h-full rounded-[9px] bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-bold text-sm items-center justify-center ${logoUrl ? 'hidden' : 'flex'}`}
              >
                {name ? name.charAt(0).toUpperCase() : 'S'}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="pt-3 pb-2 px-1 flex flex-col flex-1">
          <div className="mb-2">
            <h3 className="font-semibold text-[16px] leading-tight text-slate-900 dark:text-white line-clamp-1">
              {name}
            </h3>
            {displayLocation && (
              <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1 font-normal line-clamp-1">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{displayLocation}</span>
              </p>
            )}
          </div>
          
          <div className="flex items-center flex-wrap gap-x-3.5 gap-y-1.5 text-[13px] font-medium mt-auto">
            {/* Distance */}
            {formattedDistance && (
              <div className="flex items-center gap-1 shrink-0 text-slate-600 dark:text-slate-300">
                <Navigation className="w-[12px] h-[12px] text-emerald-600 dark:text-emerald-500 shrink-0" />
                <span>{formattedDistance}</span>
              </div>
            )}

            {/* Rating */}
            {rating !== undefined && rating > 0 && (
              <div className="flex items-center gap-1 shrink-0">
                <Star className="w-[13px] h-[13px] fill-amber-400 text-amber-400" />
                <span className="text-slate-700 dark:text-slate-300">{rating.toFixed(1)}</span>
                {reviewCount !== undefined && reviewCount > 0 && (
                  <span className="text-slate-400 font-normal ml-0.5">({reviewCount})</span>
                )}
              </div>
            )}
            
            {/* Delivery Time */}
            {deliveryTime && (
              <div className="flex items-center gap-1.5 shrink-0 text-slate-500 dark:text-slate-400">
                <Clock className="w-[13px] h-[13px] text-slate-400" />
                <span>{deliveryTime}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
